import { DomainRuleError } from './stateMachine'
import { activeLabelOf, deepFreeze } from './workflow'
import type {
  BatchReconciliation,
  Garment,
  Order,
  ReconciliationBlocker,
  ScanRead,
  TransitionId,
} from './types'

export interface ReconcileBatchInput {
  readonly id: string
  readonly orderId: string
  readonly orders: readonly Order[]
  readonly reads: readonly ScanRead[]
  readonly createdAt: string
  readonly transitionId?: TransitionId
  readonly expectedGarmentIds?: readonly string[]
}

interface IndexedScanTarget {
  readonly garment: Garment
  readonly labelStatus: 'DRAFT' | 'ACTIVE' | 'VOID'
}

const unique = (values: readonly string[]) => [...new Set(values)]

const scanKeysFor = (garment: Garment): readonly [string, IndexedScanTarget][] => {
  const pairs: [string, IndexedScanTarget][] = [[garment.shortCode, { garment, labelStatus: activeLabelOf(garment)?.status ?? 'DRAFT' }]]
  for (const label of garment.labels) {
    pairs.push([label.barcode, { garment, labelStatus: label.status }])
    pairs.push([label.epc, { garment, labelStatus: label.status }])
  }
  return pairs
}

/**
 * Pure RFID/barcode recognition. It never mutates garment workflow state.
 * Missing, duplicate, cross-order, unknown and inactive-label reads all block
 * confirmation; the UI may remove/re-read values and call this function again.
 */
export const reconcileBatchReads = (input: ReconcileBatchInput): BatchReconciliation => {
  const order = input.orders.find((candidate) => candidate.id === input.orderId)
  if (!order) throw new DomainRuleError('ORDER_NOT_FOUND', `Order ${input.orderId} was not found`)

  const expectedIds = unique(input.expectedGarmentIds ?? order.garments.map((garment) => garment.id))
  const expected = new Set(expectedIds)
  if (expectedIds.some((id) => !order.garments.some((garment) => garment.id === id))) {
    throw new DomainRuleError('EXPECTED_GARMENT_WRONG_ORDER', 'Expected garments must belong to the reconciliation order')
  }

  const scanIndex = new Map<string, IndexedScanTarget>()
  for (const candidateOrder of input.orders) {
    for (const garment of candidateOrder.garments) {
      for (const [key, target] of scanKeysFor(garment)) {
        // Active label wins over a stale/void historical version on collision.
        const existing = scanIndex.get(key)
        if (!existing || target.labelStatus === 'ACTIVE') scanIndex.set(key, target)
      }
    }
  }

  const matchedCounts = new Map<string, number>()
  const crossOrderValues: string[] = []
  const unknownValues: string[] = []
  const inactiveValues: string[] = []

  for (const read of input.reads) {
    const target = scanIndex.get(read.value)
    if (!target) {
      unknownValues.push(read.value)
      continue
    }
    if (target.garment.orderId !== order.id || !expected.has(target.garment.id)) {
      crossOrderValues.push(read.value)
      continue
    }
    if (target.labelStatus !== 'ACTIVE') {
      inactiveValues.push(read.value)
      continue
    }
    matchedCounts.set(target.garment.id, (matchedCounts.get(target.garment.id) ?? 0) + 1)
  }

  const matchedGarmentIds = expectedIds.filter((id) => (matchedCounts.get(id) ?? 0) > 0)
  const missingGarmentIds = expectedIds.filter((id) => !matchedCounts.has(id))
  const duplicateGarmentIds = expectedIds.filter((id) => (matchedCounts.get(id) ?? 0) > 1)
  const blockers: ReconciliationBlocker[] = []

  if (expectedIds.length === 0) {
    blockers.push({ code: 'EMPTY_EXPECTATION', message: '本批次没有待核对衣物', values: [] })
  }
  if (missingGarmentIds.length > 0) {
    blockers.push({ code: 'MISSING', message: `漏读 ${missingGarmentIds.length} 件`, values: missingGarmentIds })
  }
  if (duplicateGarmentIds.length > 0) {
    blockers.push({ code: 'DUPLICATE', message: `重复读取 ${duplicateGarmentIds.length} 件`, values: duplicateGarmentIds })
  }
  if (crossOrderValues.length > 0) {
    blockers.push({ code: 'CROSS_ORDER', message: `发现 ${unique(crossOrderValues).length} 个串单标签`, values: unique(crossOrderValues) })
  }
  if (unknownValues.length > 0) {
    blockers.push({ code: 'UNKNOWN', message: `发现 ${unique(unknownValues).length} 个未知标签`, values: unique(unknownValues) })
  }
  if (inactiveValues.length > 0) {
    blockers.push({ code: 'INACTIVE_LABEL', message: `发现 ${unique(inactiveValues).length} 个未激活/已作废标签`, values: unique(inactiveValues) })
  }

  return deepFreeze({
    id: input.id,
    orderId: order.id,
    transitionId: input.transitionId,
    expectedGarmentIds: expectedIds,
    matchedGarmentIds,
    missingGarmentIds,
    reads: [...input.reads],
    blockers,
    canConfirm: blockers.length === 0,
    createdAt: input.createdAt,
  }) as BatchReconciliation
}

export const assertReconciliationCanConfirm = (reconciliation: BatchReconciliation): void => {
  if (!reconciliation.canConfirm || reconciliation.blockers.length > 0) {
    throw new DomainRuleError(
      'RECONCILIATION_BLOCKED',
      'Batch cannot be confirmed until all reconciliation blockers are cleared',
      reconciliation.blockers.map((blocker) => `${blocker.code}: ${blocker.message}`),
    )
  }
  if (reconciliation.confirmedAt) {
    throw new DomainRuleError('RECONCILIATION_ALREADY_CONFIRMED', `Batch ${reconciliation.id} was already confirmed`)
  }
}

